import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import websocketService from '../../../services/websocketService';
import {
  fetchEvent,
  fetchMyEvents,
  respondToEvent,
} from '../services/eventService';

const EVENT_INVITE_EVENT = 'workloop:event-invite';

const pendingViewer = new Set(['INVITED', 'PENDING', 'TENTATIVE', '']);

const isPendingInvite = (event) => {
  if (!event?.id) return false;
  if (event.viewerIsCreator) return false;
  const status = String(event.status || '').toUpperCase();
  if (status === 'DRAFT' || status === 'CANCELLED') return false;
  const viewer = String(event.viewerStatus || '').toUpperCase();
  return pendingViewer.has(viewer);
};

const eventIdFromPayload = (data) =>
  String(data?.eventId || data?.id || data?.event_id || '').trim();

const looksLikeInvite = (data) => {
  if (!data) return false;
  const type = String(data.type || '').toUpperCase();
  const eventType = String(data.eventType || '').toUpperCase();
  return type === 'EVENT_INVITE'
    || type === 'EVENT_INVITATION'
    || type === 'EVENT_INVITED'
    || type === 'EVENT_PUBLISHED'
    || eventType === 'EVENT_PUBLISHED';
};

const EventInvitePopup = () => {
  const { token, user } = useSelector((state) => state.auth);
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const shownRef = useRef(new Set());
  const currentRef = useRef(null);

  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  const enqueue = useCallback((incoming) => {
    const id = String(incoming?.id || incoming?.eventId || '').trim();
    if (!id || shownRef.current.has(id)) return;
    shownRef.current.add(id);
    setQueue((prev) => {
      if (prev.some((item) => String(item.id) === id)) return prev;
      if (currentRef.current && String(currentRef.current.id) === id) return prev;
      return [...prev, { ...incoming, id }];
    });
  }, []);

  const scanPending = useCallback(async () => {
    if (!token) return;
    try {
      const list = await fetchMyEvents();
      (Array.isArray(list) ? list : []).filter(isPendingInvite).forEach((event) => {
        enqueue(event);
      });
    } catch {
      // Network may not be ready.
    }
  }, [enqueue, token]);

  useEffect(() => {
    if (!token) return undefined;
    scanPending();
    const interval = window.setInterval(scanPending, 12000);
    return () => window.clearInterval(interval);
  }, [token, scanPending]);

  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue((prev) => prev.slice(1));
      setError('');
    }
  }, [current, queue]);

  useEffect(() => {
    const id = current?.id;
    if (!id || current?.name || current?.displayTitle) return undefined;
    let cancelled = false;
    fetchEvent(id)
      .then((dto) => {
        if (cancelled || !dto) return;
        if (!isPendingInvite(dto)) {
          setCurrent(null);
          return;
        }
        setCurrent((prev) => (prev && String(prev.id) === String(dto.id) ? { ...prev, ...dto } : prev));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [current?.id, current?.name, current?.displayTitle]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const handlePayload = (data) => {
      if (!looksLikeInvite(data)) return;
      const eventId = eventIdFromPayload(data);
      if (!eventId) {
        scanPending();
        return;
      }
      enqueue({
        id: eventId,
        name: data.eventName || data.ticketTitle || data.title || data.name,
        description: data.description || data.body,
        creatorName: data.inviterName || data.creatorName || data.actorName,
        eventType: data.eventKind || data.kind,
      });
    };

    const onCustom = (event) => handlePayload(event?.detail || {});
    window.addEventListener(EVENT_INVITE_EVENT, onCustom);

    const eventsTopic = `/topic/events/${user.id}`;
    websocketService.subscribeToTopic('/user/queue/events', handlePayload);
    websocketService.subscribeToTopic(eventsTopic, handlePayload);

    return () => {
      window.removeEventListener(EVENT_INVITE_EVENT, onCustom);
    };
  }, [enqueue, scanPending, user?.id]);

  const closeCurrent = () => {
    setBusy(false);
    setError('');
    setCurrent(null);
  };

  const respond = async (response) => {
    if (!current?.id || busy) return;
    setBusy(true);
    setError('');
    try {
      await respondToEvent(current.id, { response });
      closeCurrent();
    } catch (err) {
      setBusy(false);
      setError(err?.response?.data?.message || err?.message || 'Could not respond');
    }
  };

  if (!current) return null;

  const who = current.creatorName || current.inviterName || 'Someone';
  const title = current.displayTitle || current.name || current.eventName || 'an event';
  const description = current.description || '';

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center p-4 bg-slate-900/55"
      role="presentation"
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl outline-none dark:bg-gray-900"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="event-invite-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-2 w-full bg-gradient-to-r from-violet-500 via-indigo-500 to-sky-400" />
        <div className="p-6 sm:p-7">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
            Event invitation
          </p>
          <h3
            id="event-invite-title"
            className="text-xl font-bold leading-tight text-slate-900 dark:text-white sm:text-2xl"
          >
            {who} invited you to {title}
          </h3>
          {description ? (
            <p className="mt-3 whitespace-pre-wrap break-words text-sm text-slate-600 dark:text-slate-300">
              {description}
            </p>
          ) : null}
          {error ? (
            <p className="mt-3 text-sm text-rose-600">{error}</p>
          ) : null}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => respond('ACCEPTED')}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? '…' : 'Join'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => respond('DECLINED')}
              className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
            >
              Decline
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={closeCurrent}
              className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export { EVENT_INVITE_EVENT };
export default EventInvitePopup;
